import {
  fetchClients,
  fetchRateOverrides,
  fetchSites,
  fetchWorkTypes,
} from './client.js';
import {
  listClientsCached,
  replaceClients,
  replaceRateOverrides,
  replaceSites,
  replaceWorkTypes,
} from '../db/jobsDb.js';
import { patchSyncFreshness } from '../sync/freshness.js';

export type ReferenceCacheResult = {
  ok: boolean;
  error: string | null;
  lastOkAt: string | null;
};

/** Refresh dropdown caches when online + authenticated. Never blocks the form. */
export async function refreshReferenceCache(): Promise<ReferenceCacheResult> {
  try {
    const [clients, types, sites, overrides, existing] = await Promise.all([
      fetchClients(),
      fetchWorkTypes(),
      fetchSites(),
      fetchRateOverrides(),
      listClientsCached(),
    ]);
    const lastUsed = new Map(
      existing.map((c) => [c.id, c.lastUsedAt] as const),
    );
    await replaceClients(
      clients.map((c) => ({
        id: c.id,
        name: c.name,
        day_rate: c.day_rate ?? null,
        half_day_rate: c.half_day_rate ?? null,
        hourly_rate: c.hourly_rate ?? null,
        contact_name: c.contact_name ?? null,
        contact_email: c.contact_email ?? null,
        address: c.address ?? null,
        payment_terms_days: c.payment_terms_days ?? null,
        external_contact_id: c.external_contact_id ?? null,
        xero_contact_status: c.xero_contact_status ?? (c.external_contact_id ? 'mapped' : 'unmapped'),
        xero_contact_error: c.xero_contact_error ?? null,
        xero_contact_matches: c.xero_contact_matches ?? null,
        lastUsedAt: lastUsed.get(c.id) ?? null,
      })),
    );
    await replaceWorkTypes(
      types.map((t) => ({
        id: t.id,
        code: t.code,
        short_description: t.short_description,
        line_kind: t.line_kind ?? 'day_rate',
        invoice_narrative: t.invoice_narrative ?? null,
        half_day_suffix: t.half_day_suffix ?? null,
      })),
    );
    await replaceSites(
      sites.map((s) => ({
        id: s.id,
        name: s.name,
        client_id: s.client_id,
        useCount: Number(s.use_count ?? 0),
        lastUsedAt: null,
      })),
    );
    await replaceRateOverrides(
      overrides.map((o) => ({
        id: o.id,
        client_id: o.client_id,
        work_type_id: o.work_type_id,
        day_rate: o.day_rate,
        half_day_rate: o.half_day_rate,
        hourly_rate: o.hourly_rate,
      })),
    );
    const at = new Date().toISOString();
    await patchSyncFreshness({
      referenceAt: at,
      referenceError: null,
    });
    return { ok: true, error: null, lastOkAt: at };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('refreshReferenceCache failed', err);
    const freshness = await patchSyncFreshness({
      referenceError: message,
    });
    return {
      ok: false,
      error: message,
      lastOkAt: freshness.referenceAt,
    };
  }
}
